import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/** One of several: chosen with `onSelect`, never un-chosen by itself. */
interface RadioChoice {
  type: 'radio'
  /** Shared by every choice in the group, so the arrow keys move between them. */
  name: string
  onSelect: () => void
}

/** Any of several: toggled with `onCheckedChange`. */
interface CheckboxChoice {
  type: 'checkbox'
  name?: string
  onCheckedChange: (checked: boolean) => void
}

interface ChoiceContent {
  checked: boolean
  /** The choice in a few words. */
  title: React.ReactNode
  /** What choosing it means, in a sentence. */
  description?: React.ReactNode
  /** An icon or a framed thumbnail, between the mark and the words. */
  media?: React.ReactNode
  /** What sits at the end of the line: a price, a badge, a count. */
  aside?: React.ReactNode
  /** Anything else under the description — a status line, a warning. */
  children?: React.ReactNode
  /** A choice that cannot be made now. Say why in its description. */
  disabled?: boolean
  value?: string
  id?: string
  'aria-describedby'?: string
  className?: string
}

export type ChoiceProps = ChoiceContent & (RadioChoice | CheckboxChoice)

export type ChoiceCardProps = ChoiceProps & {
  /** `compact` for a grid of short choices that need no description. */
  size?: 'default' | 'compact'
}

/**
 * The mark that says what kind of choice this is: round for one of several,
 * square for any of several — the same shapes as `Radio` and `Checkbox`, drawn
 * here because the real input is visually hidden and the whole card is its face.
 */
function ChoiceMark({ type, checked }: { type: 'radio' | 'checkbox'; checked: boolean }) {
  if (type === 'radio') {
    return (
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-round border transition-colors duration-100 ease-out motion-reduce:transition-none',
          checked ? 'border-primary bg-primary' : 'border-line-strong bg-surface',
        )}
      >
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-round bg-surface transition-transform duration-100 ease-out motion-reduce:transition-none',
            checked ? 'scale-100' : 'scale-0',
          )}
        />
      </span>
    )
  }
  return (
    <span
      aria-hidden
      className={cn(
        'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-sm border transition-colors duration-100 ease-out motion-reduce:transition-none',
        checked ? 'border-primary bg-primary text-on-primary' : 'border-line-strong bg-surface',
      )}
    >
      {checked ? <Check className="h-3 w-3 stroke-[3]" /> : null}
    </span>
  )
}

/**
 * The native input under a choice, visually hidden rather than replaced by a
 * `div` with a role: keyboard operation (arrow keys between radios that share a
 * `name`, space on a checkbox) and the screen reader's "radio, 2 of 3" come free
 * from the real control.
 */
function ChoiceInput({ choice, labelledBy, describedBy }: { choice: ChoiceProps; labelledBy: string; describedBy: string | undefined }) {
  const shared = {
    className: 'sr-only',
    checked: choice.checked,
    disabled: choice.disabled,
    value: choice.value,
    id: choice.id,
    'aria-labelledby': labelledBy,
    'aria-describedby': describedBy,
  }
  if (choice.type === 'radio') {
    const { onSelect } = choice
    return (
      <input
        type="radio"
        name={choice.name}
        {...shared}
        onChange={(event) => {
          if (event.target.checked) onSelect()
        }}
      />
    )
  }
  const { onCheckedChange } = choice
  return <input type="checkbox" name={choice.name} {...shared} onChange={(event) => onCheckedChange(event.target.checked)} />
}

/**
 * Everything inside a choice, shared by the card and the row: the hidden input,
 * the mark, any media, the words and the aside.
 *
 * The input is named by the title alone and described by the rest, so a screen
 * reader says "Saturday, radio, 1 of 2" and then the price and what it
 * includes — not the whole card run together as one name.
 */
function ChoiceBody({ choice, dense }: { choice: ChoiceProps; dense: boolean }) {
  const { type, checked, title, description, media, aside, children } = choice
  const id = React.useId()
  const parts = {
    title: `${id}-title`,
    description: description ? `${id}-description` : null,
    more: children ? `${id}-more` : null,
    aside: aside ? `${id}-aside` : null,
  }
  const describedBy = [parts.description, parts.aside, parts.more, choice['aria-describedby'] ?? null]
    .filter((part) => part !== null)
    .join(' ')
  return (
    <>
      <ChoiceInput choice={choice} labelledBy={parts.title} describedBy={describedBy === '' ? undefined : describedBy} />
      <ChoiceMark type={type} checked={checked} />
      {media}
      <span className="min-w-0 flex-1">
        <span id={parts.title} className="block text-sm font-medium text-ink">
          {title}
        </span>
        {parts.description === null ? null : (
          // A card's description is body text; a row's, in a dense list, is the small size.
          <span id={parts.description} className={cn('mt-0.5 block text-ink-muted', dense ? 'text-small' : 'text-sm')}>
            {description}
          </span>
        )}
        {parts.more === null ? null : (
          <span id={parts.more} className="block">
            {children}
          </span>
        )}
      </span>
      {parts.aside === null ? null : (
        <span id={parts.aside} className="shrink-0">
          {aside}
        </span>
      )}
    </>
  )
}

/**
 * Plan View's selectable card: a white panel on the hairline that you choose by
 * clicking anywhere on it. Chosen, it takes the selection pair — a `primary`
 * edge on `accent-soft` — and its mark fills. No shadow: nothing casts one but
 * floating layers.
 *
 * Reach for it whenever the choice is the point of the screen and each answer
 * needs a sentence to be understood: a plan, a package, what happens to the
 * accounts. A view or mode switch of two or three words is a `SegmentedControl`;
 * a long list is a `Select`. A plain `Radio` is for a matrix, where each cell is
 * one answer and a card per cell would be noise.
 *
 * Group them in a `ChoiceGroup`, which names the question.
 */
export function ChoiceCard(props: ChoiceCardProps) {
  const { checked, disabled = false, size = 'default', className } = props
  return (
    <label
      className={cn(
        'flex min-w-0 items-start gap-3 rounded-lg border text-left transition-colors duration-100 ease-out has-[:focus-visible]:focus-ds motion-reduce:transition-none',
        size === 'compact' ? 'px-3 py-2.5' : 'p-4',
        checked ? 'border-primary bg-accent-soft' : 'border-line bg-surface',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        !checked && !disabled && 'hover:border-line-strong',
        // A chosen card that can no longer change stays legible: it is the answer.
        disabled && !checked && 'opacity-60',
        className,
      )}
    >
      <ChoiceBody choice={props} dense={false} />
    </label>
  )
}

interface ChoiceGroupProps {
  /** The question the choices answer, as the group's legend. */
  legend: React.ReactNode
  /** Keep the legend for screen readers only, where a heading above already asks. */
  hideLegend?: boolean
  /** What to know before choosing, under the legend: a line of text, or what the choice is about (a list of plans). */
  hint?: React.ReactNode
  /** Columns from the small breakpoint up; one column below it. */
  columns?: 1 | 2 | 3
  className?: string
  children: React.ReactNode
}

const COLUMNS = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const

/**
 * A question and its `ChoiceCard`s: a fieldset whose legend names the group, so
 * a screen reader announces the question before the first answer.
 */
export function ChoiceGroup({ legend, hideLegend = false, hint, columns = 1, className, children }: ChoiceGroupProps) {
  return (
    <fieldset className={cn('min-w-0', className)}>
      <legend className={cn('mb-2 p-0 text-control font-medium text-ink', hideLegend && 'sr-only')}>{legend}</legend>
      {hint ? <div className="-mt-1 mb-2 text-small text-ink-muted">{hint}</div> : null}
      <div className={cn('grid gap-2', COLUMNS[columns])}>{children}</div>
    </fieldset>
  )
}

/**
 * A choice as a row of a `ChoiceList`: the same mark and words as a card, inside
 * one bordered list rather than a panel of its own — for picking one of a set of
 * records (which lead, which booking), where the rows are the content.
 */
export function ChoiceRow(props: ChoiceProps) {
  const { checked, disabled = false, className } = props
  return (
    <label
      className={cn(
        'flex min-w-0 items-start gap-3 px-3 py-2.5 text-left transition-colors duration-100 ease-out has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--ds-focus)] motion-reduce:transition-none',
        checked ? 'bg-accent-soft' : 'bg-surface',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        !checked && !disabled && 'hover:bg-row-hover',
        disabled && !checked && 'opacity-60',
        className,
      )}
    >
      <ChoiceBody choice={props} dense />
    </label>
  )
}

interface ChoiceListProps {
  /** Names the group for assistive tech. */
  label: string
  /** Checkboxes rather than radios. */
  multiple?: boolean
  className?: string
  children: React.ReactNode
}

/** The bordered list that holds `ChoiceRow`s, one hairline between each. */
export function ChoiceList({ label, multiple = false, className, children }: ChoiceListProps) {
  return (
    <div
      role={multiple ? 'group' : 'radiogroup'}
      aria-label={label}
      className={cn('divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface', className)}
    >
      {children}
    </div>
  )
}
